/**
 * Push soft-prompt event bus.
 *
 * Two high-value moments can ask for push permission, but neither lives in a
 * component that can render a sheet:
 *   - `streak_started`  — fired from `addXP()`, a plain async helper.
 *   - `first_food_log`  — fired from a mutation's `onSuccess`.
 *
 * So they emit onto this bus and `PushPromptHost` (mounted once, app-wide)
 * renders the sheet. The host still runs the normal eligibility gate, so
 * cool-off / max-asks / OS-already-prompted all apply — these surfaces add
 * more CHANCES inside the existing discipline, they never bypass it.
 */

export type PushPromptTrigger = 'streak_started' | 'first_food_log';
export type PushPromptSurface = 'streak' | 'nutrition';

export interface PushPromptEvent {
  trigger: PushPromptTrigger;
  surface: PushPromptSurface;
}

const SURFACE_FOR: Record<PushPromptTrigger, PushPromptSurface> = {
  streak_started: 'streak',
  first_food_log: 'nutrition',
};

/** Per-user, per-trigger "this moment has been used" flag. */
const firedKey = (trigger: PushPromptTrigger, userId: string) =>
  `push_prompt_fired.${trigger}.${userId}`;

export function hasPushPromptFired(trigger: PushPromptTrigger, userId: string): boolean {
  try {
    return localStorage.getItem(firedKey(trigger, userId)) === 'true';
  } catch {
    return false;
  }
}

type Listener = (event: PushPromptEvent) => void;
const listeners = new Set<Listener>();

export function subscribeToPushPrompts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Emit a one-shot push prompt moment. The fired flag is persisted here, at
 * the MOMENT, not at display time: a streak only starts once and a first food
 * log only happens once, so a moment that arrives while the user is cooling
 * off is spent rather than queued up to nag them later.
 *
 * Returns true when the event was emitted (first time for this user).
 */
export function emitPushPrompt(trigger: PushPromptTrigger, userId: string | undefined): boolean {
  if (!userId) return false;
  if (hasPushPromptFired(trigger, userId)) return false;
  try {
    localStorage.setItem(firedKey(trigger, userId), 'true');
  } catch {
    /* storage unavailable — still emit, we just can't dedupe across reloads */
  }
  const event: PushPromptEvent = { trigger, surface: SURFACE_FOR[trigger] };
  listeners.forEach((l) => {
    try {
      l(event);
    } catch {
      /* a bad listener must not break the caller's flow */
    }
  });
  return true;
}
